<script>
import FormInputContainerMixin from '@mixins/FormInputContainerMixin'
import VNodes from '@components/VNodes'
import {
  TTS_MODE,
  UPLOAD_MODE,
  resolveAudioOrAutomatedVoiceSaveUpdates,
} from '@helpers/audioOrAutomatedVoiceHelpers'
import { get } from 'lodash'
import { getDescendantComponentInstances, getProp, updateComponentOptions } from '@helpers/NodeHelper'
import { getTranslationsFrom } from '@helpers/TranslationHelper'
import { snakeCaseToCamelCaseKeepingSpecialCharacters } from '@helpers/StringHelper'

const UPLOAD_AUDIO_MODE = 'upload_audio'
const GENERATE_AUDIO = 'generate_audio_from_text'
const AUDIO_REPLACED_WITH_TRANSCRIPT_WARNING =
  'You have at least one audio file not in use. Download the audio if you think you may need it in the future. Saving changes will delete the uploaded audio that isn\'t in use.'

export default {
  name: 'AudioOrAutomatedPanel',
  components: {
    VNodes,
  },
  mixins: [FormInputContainerMixin],
  props: {
    field: { type: String, required: true },
    generatedAudioScriptField: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    hint: { type: [String, Boolean], default: false },
    label: { type: [Boolean, String], default: false },
    voiceTalent: { type: Object, default: null },

    // Shows the Generate Automated Voice button on default
    allowGeneratedAudio: { type: Boolean, default: true },
  },
  data () {
    return {
      UPLOAD_AUDIO_MODE,
      GENERATE_AUDIO,
      mode: UPLOAD_AUDIO_MODE,
      stashedAudio: null,
      stashedScript: null,
      initialAudioValue: null,
      initialScriptValue: null,
      isInitializing: true,
    }
  },
  computed: {
    resolvedGeneratedAudioScriptField () {
      return this.generatedAudioScriptField || `${this.field}_generated_audio_script`
    },
    audioFieldProp () {
      return snakeCaseToCamelCaseKeepingSpecialCharacters(this.field)
    },
    generatedAudioScriptFieldProp () {
      return snakeCaseToCamelCaseKeepingSpecialCharacters(this.resolvedGeneratedAudioScriptField)
    },
    helperMode () {
      return this.isUploadMode ? UPLOAD_MODE : TTS_MODE
    },
    audioValue () {
      const value = get(this.model, this.audioFieldProp)
      return value === undefined ? null : value
    },
    generatedAudioScriptValue () {
      const value = get(this.model, this.generatedAudioScriptFieldProp)
      return value === undefined ? null : value
    },
    translationOptions () {
      return { prefix: this.prefix || this.$attrs['data-model'] }
    },
    fieldLabel () {
      if (this.label) return this.label
      return getTranslationsFrom(this.field, {}, this.translationOptions).label
    },
    isUploadMode () {
      return this.mode === UPLOAD_AUDIO_MODE
    },
    modeOptions () {
      const options = [{ label: 'Upload Audio File', value: UPLOAD_AUDIO_MODE }]
      if (this.allowGeneratedAudio) options.push({ label: 'Generate Automated Voice', value: GENERATE_AUDIO })
      return options
    },
  },
  watch: {
    mode () {
      if (this.isInitializing) return
      this.onModeChange()
      this.$emit('change')
    },
    allowGeneratedAudio (allowed) {
      if (allowed || this.isUploadMode) return
      this.mode = this.UPLOAD_AUDIO_MODE
    },
  },
  created () {
    this.initialAudioValue = this.audioValue
    this.initialScriptValue = this.generatedAudioScriptValue
    this.mode = this.inferInitialMode()
  },
  mounted () {
    this.$nextTick(() => {
      this.isInitializing = false
    })
  },
  methods: {
    inferInitialMode () {
      if (this.generatedAudioScriptValue) return GENERATE_AUDIO
      if (!this.allowGeneratedAudio) return UPLOAD_AUDIO_MODE
      if (!this.hasAudioValue(this.audioValue) && this.generatedAudioScriptValue) return GENERATE_AUDIO
      return UPLOAD_AUDIO_MODE
    },
    hasAudioValue (audioValue) {
      return Boolean(
        (audioValue && audioValue.name) || (audioValue && audioValue.url) ||
        (typeof audioValue === 'string' && audioValue)
      )
    },
    scriptValuesEqual (a, b) {
      return (a == null && b == null) || a === b
    },
    audioFileKey (audioValue) {
      if (!this.hasAudioValue(audioValue)) return null
      if (typeof audioValue === 'string') return audioValue
      return audioValue.name || audioValue.url || null
    },
    audioValuesEqual (a, b) {
      return this.audioFileKey(a) === this.audioFileKey(b)
    },
    initializeSlotChildren (slotName) {
      const slotContent = this.$slots[slotName]
      if (!slotContent) return []

      const isActive = slotName === 'upload' ? this.isUploadMode : !this.isUploadMode
      return slotContent.map(vNode => this.setupSlotChild(vNode, isActive, slotName))
    },
    setupSlotChild (vNode, isActive, slotName) {
      const configured = this.setupNodeAndChildren(vNode)

      if (!this.isFormInput(configured)) return configured

      const existingDisabled = getProp(configured, 'disabled')
      const propsData = {
        disabled: this.disabled || existingDisabled || !isActive,
      }

      if (slotName === 'generate') {
        propsData.voiceTalent = this.voiceTalent
        propsData.audioField = this.field
        propsData.model = this.model
      }

      return updateComponentOptions(configured, { propsData })
    },
    onFormInputValueChange (fieldProp, value) {
      if (fieldProp === this.audioFieldProp) {
        this.handleAudioFieldChange(value)
      } else if (fieldProp === this.generatedAudioScriptFieldProp) {
        this.handleScriptFieldChange(value)
      } else {
        this.$emit('input', fieldProp, value)
      }
    },
    handleAudioFieldChange (value) {
      if (this.audioValuesEqual(value, this.audioValue)) return

      if (this.isUserUploadedFile(value)) {
        this.replaceGeneratedContentWithUpload(value)
        return
      }

      if (this.generatedAudioScriptValue != null) {
        if (this.stashedScript == null) {
          this.stashedScript = this.generatedAudioScriptValue
        }
        this.$emit('input', this.generatedAudioScriptFieldProp, null)
      }
      this.$emit('input', this.audioFieldProp, value)
    },
    handleScriptFieldChange (value) {
      if (this.scriptValuesEqual(value, this.generatedAudioScriptValue)) return

      if (value == null && this.isUploadMode) {
        this.$emit('input', this.generatedAudioScriptFieldProp, null)
        this.resetAutomatedVoiceInput()
        return
      }

      if (this.hasAudioValue(this.audioValue)) {
        if (this.stashedAudio == null) {
          this.stashedAudio = this.audioValue
        }
        this.$emit('input', this.audioFieldProp, null)
      }
      this.$emit('input', this.generatedAudioScriptFieldProp, value)
    },
    onModeChange () {
      if (this.isUploadMode) {
        const audioStashToRestore = this.hasActiveGeneratedTts() ? null : this.stashedAudio
        const isRestoringStashedAudio = audioStashToRestore != null
        this.restoreStashedField({
          stashedValue: audioStashToRestore,
          fieldProp: this.audioFieldProp,
          clearStash: () => { this.stashedAudio = null },
          opposingValue: this.generatedAudioScriptValue,
          opposingStash: value => { this.stashedScript = value },
          opposingFieldProp: this.generatedAudioScriptFieldProp,
        })
        if (!isRestoringStashedAudio) {
          this.revertUnsavedScriptWhenSwitchingToUpload()
        }
        // Clear eagerly — disabled (and its error suppressor) updates on the next render.
        this.onInputValid(this.generatedAudioScriptFieldProp)
      } else {
        const isRestoringStashedScript = this.stashedScript != null
        this.restoreStashedField({
          stashedValue: this.stashedScript,
          fieldProp: this.generatedAudioScriptFieldProp,
          clearStash: () => { this.stashedScript = null },
          opposingValue: this.audioValue,
          opposingStash: value => { this.stashedAudio = value },
          opposingFieldProp: this.audioFieldProp,
        })
        if (!isRestoringStashedScript) {
          this.revertUnsavedAudioWhenSwitchingToGenerate()
        }
      }
    },
    restoreStashedField ({ stashedValue, fieldProp, clearStash, opposingValue, opposingStash, opposingFieldProp }) {
      if (stashedValue == null) return

      clearStash()
      this.$emit('input', fieldProp, stashedValue)

      if (this.hasMutuallyExclusiveOpposingValue(opposingValue, opposingFieldProp)) {
        if (this.opposingStashIsEmpty(opposingFieldProp)) {
          opposingStash(opposingValue)
        }
        this.$emit('input', opposingFieldProp, null)
      }
    },
    opposingStashIsEmpty (fieldProp) {
      if (fieldProp === this.generatedAudioScriptFieldProp) return this.stashedScript == null
      if (fieldProp === this.audioFieldProp) return this.stashedAudio == null
      return true
    },
    revertUnsavedScriptWhenSwitchingToUpload () {
      if (this.scriptValuesEqual(this.generatedAudioScriptValue, this.initialScriptValue)) return
      if (this.shouldKeepScriptClearedForUpload()) return

      if (this.stashedScript == null) {
        this.stashedScript = this.generatedAudioScriptValue
      }
      this.$emit('input', this.generatedAudioScriptFieldProp, this.initialScriptValue)
    },
    revertUnsavedAudioWhenSwitchingToGenerate () {
      if (this.audioValuesEqual(this.audioValue, this.initialAudioValue)) return
      if (this.shouldKeepAudioClearedForGenerate()) return

      if (this.stashedAudio == null) {
        this.stashedAudio = this.audioValue
      }
      this.$emit('input', this.audioFieldProp, this.initialAudioValue)
    },
    shouldKeepScriptClearedForUpload () {
      return this.hasAudioValue(this.audioValue) ||
        this.stashedAudio != null ||
        (this.generatedAudioScriptValue == null && this.stashedScript != null)
    },
    shouldKeepAudioClearedForGenerate () {
      return (this.generatedAudioScriptValue != null &&
        !this.scriptValuesEqual(this.generatedAudioScriptValue, this.initialScriptValue)) ||
        this.stashedScript != null ||
        (!this.hasAudioValue(this.audioValue) && this.stashedAudio != null) ||
        this.isUserUploadedFile(this.audioValue)
    },
    hasMutuallyExclusiveOpposingValue (value, fieldProp) {
      if (fieldProp === this.audioFieldProp) {
        return this.hasAudioValue(value)
      }

      return value != null && value !== ''
    },
    shouldHideGeneratedAudioOnUpload () {
      if (!this.isUploadMode) return false
      if (this.generatedAudioScriptValue == null || this.generatedAudioScriptValue === '') return false
      return this.hasAudioValue(this.audioValue)
    },
    formInputConfig (fieldProp) {
      const config = FormInputContainerMixin.methods.formInputConfig.call(this, fieldProp)

      if (!this.shouldHideGeneratedAudioOnUpload() || fieldProp !== this.audioFieldProp) return config

      return {
        ...config,
        propsData: {
          ...config.propsData,
          value: null,
        },
      }
    },
    replacesAudioWithTranscript (initialModel) {
      if (!initialModel) return false

      const initialAudio = get(initialModel, this.audioFieldProp)
      if (!this.hasAudioValue(initialAudio)) return false

      return this.generatedAudioScriptValue != null &&
        !this.hasAudioValue(this.audioValue)
    },
    getConfirmChangesWarningMessage (initialModel) {
      return this.replacesAudioWithTranscript(initialModel)
        ? AUDIO_REPLACED_WITH_TRANSCRIPT_WARNING
        : null
    },
    automatedVoiceInput () {
      return getDescendantComponentInstances(this, 'AutomatedVoiceFormInput')[0]
    },
    isUserUploadedFile (value) {
      return Boolean(value?.file)
    },
    replaceGeneratedContentWithUpload (uploadedValue) {
      this.stashedScript = null
      this.stashedAudio = null
      this.resetAutomatedVoiceInput()

      if (!this.scriptValuesEqual(null, this.generatedAudioScriptValue)) {
        this.$emit('input', this.generatedAudioScriptFieldProp, null)
      }
      this.$emit('input', this.audioFieldProp, uploadedValue)
    },
    resetAutomatedVoiceInput () {
      const ttsInput = this.automatedVoiceInput()
      if (ttsInput?.resetGeneratedVoiceState) ttsInput.resetGeneratedVoiceState()
    },
    clearGeneratedContentStashes () {
      this.stashedAudio = null
      this.stashedScript = null
    },
    clearUploadedAudioStash () {
      this.stashedAudio = null
    },
    hasActiveGeneratedTts () {
      const script = this.generatedAudioScriptValue
      if (!script?.trim()) return false

      const ttsInput = this.automatedVoiceInput()
      return ttsInput?.generatedFromText === script && this.hasAudioValue(this.audioValue)
    },
    resolveSaveUpdates (initialModel) {
      if (!initialModel) return null

      return resolveAudioOrAutomatedVoiceSaveUpdates({
        model: this.model,
        initialModel,
        mode: this.helperMode,
        audioField: this.field,
        ttsField: this.resolvedGeneratedAudioScriptField,
        generatedFromText: this.automatedVoiceInput()?.generatedFromText ?? null,
      })
    },
    prepareSavePayload (payload, initialModel) {
      const updates = this.resolveSaveUpdates(initialModel)
      if (!updates) return payload

      const preparedPayload = { ...payload, ...updates }

      // When upload-mode exclusivity nulls TTS but the confirm diff included both
      // fields, keep the generated TTS value from the modal diff.
      if (
        updates[this.generatedAudioScriptFieldProp] === null &&
        payload[this.generatedAudioScriptFieldProp] &&
        payload[this.audioFieldProp]
      ) {
        preparedPayload[this.generatedAudioScriptFieldProp] =
          payload[this.generatedAudioScriptFieldProp]
      }

      return preparedPayload
    },
  },
}
</script>

<template>
  <div
    class="audio-or-automated-panel form-control"
    :data-audio-field="field"
  >
    <Label v-if="fieldLabel" v-bind="{ required, hint }">{{ fieldLabel }}</Label>
    <div class="audio-or-automated-panel__fields">
      <ButtonGroupSelectInput
        v-model="mode"
        class="audio-or-automated-panel__mode-select"
        :items="modeOptions"
        singleRow
        :label="false"
        :required="required"
        :disabled="disabled"
      />
      <div class="audio-or-automated-panel__section-container">
        <!-- v-show (not :key) keeps unsaved generate state across tab switches. -->
        <div
          v-show="isUploadMode"
          class="audio-or-automated-panel__section"
        >
          <VNodes :content="initializeSlotChildren('upload')"/>
        </div>
        <div
          v-show="!isUploadMode"
          class="audio-or-automated-panel__section"
        >
          <VNodes :content="initializeSlotChildren('generate')"/>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.audio-or-automated-panel.form-control {
  margin-bottom: 32px;
}

.audio-or-automated-panel__fields {
  @include bordered-box;

  padding: 16px;
}

.audio-or-automated-panel__mode-select {
  margin-bottom: 16px;
}

.audio-or-automated-panel__section {
  ::v-deep .form-control {
    margin-bottom: 0;
  }
}
</style>
